import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { buildIntegrationPrompt } from '@admin-platform/shared/integration-content';
import { endpointsForPage } from '../api-endpoints';
import { AnchorHeading } from '../components/AnchorHeading';
import { OnThisPage } from '../components/OnThisPage';
import { useDocsPage } from '../hooks/useDocsPage';

const AGENT_PROMPT = buildIntegrationPrompt('supportWidget');

const CLIENT = `// A minimal client for the support chat API. getToken() calls the route on
// your backend that mints a token (see "Sign users in to the support chat").
const CHAT_URL = 'https://api.revvly.ai/api/v1/support/graphql';
let session = null; // { token, expiresAt }

async function token() {
  if (!session || new Date(session.expiresAt).getTime() - Date.now() < 60_000) {
    session = await getToken(); // POST /api/support-token on your backend
  }
  return session.token;
}

export async function chat(query, variables = {}, retried = false) {
  const res = await fetch(CHAT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: \`Bearer \${await token()}\` },
    body: JSON.stringify({ query, variables }),
  });
  const { data, errors } = await res.json();
  if (errors?.[0]?.extensions?.code === 'UNAUTHENTICATED' && !retried) {
    session = null; // expired or revoked: mint a fresh token and try once more
    return chat(query, variables, true);
  }
  if (errors) throw Object.assign(new Error(errors[0].message), errors[0].extensions);
  return data;
}`;

const ME = `query {
  me { externalUserId name email role unreadCount unreadTickets }
  myNotificationPreferences { progressUpdates emailWhenOffline }
  ticketTypes { id key name }
}`;

const ME_RESPONSE = `{
  "data": {
    "me": {
      "externalUserId": "user_8412",
      "name": "Ana Silva",
      "email": "ana@acme.com",
      "role": "founder",
      "unreadCount": 2,
      "unreadTickets": 1
    },
    "myNotificationPreferences": { "progressUpdates": "ALL", "emailWhenOffline": true },
    "ticketTypes": [
      { "id": "66f0…a1", "key": "question", "name": "Question" },
      { "id": "66f0…a2", "key": "issue_report", "name": "Issue report" },
      { "id": "66f0…a3", "key": "suggestion", "name": "Suggestion" }
    ]
  }
}`;

const MY_TICKETS = `query MyTickets($after: String) {
  myTickets(first: 20, after: $after) {
    nodes {
      id
      number
      title
      type { name }
      status { name category }
      lastMessageAt
      unreadCount
      progress { label }
    }
    pageInfo { hasNextPage endCursor }
  }
}`;

const TICKET = `query Ticket($id: ID!) {
  ticket(id: $id) {
    id
    number
    title
    status { key name category }
    createdAt
    closedAt
    unreadCount
    progress { key label reachedAt }
  }
}`;

const MESSAGES = `query Thread($ticketId: ID!, $before: String) {
  ticketMessages(ticketId: $ticketId, first: 50, before: $before) {
    nodes { id author body createdAt }
    pageInfo { hasPreviousPage startCursor }
  }
}`;

const MESSAGES_RESPONSE = `{
  "data": {
    "ticketMessages": {
      "nodes": [
        { "id": "6701…01", "author": "YOU", "body": "The export button does nothing.", "createdAt": "2026-10-05T09:12:03.000Z" },
        { "id": "6701…02", "author": "AGENT", "body": "Thanks Ana, looking at it now.", "createdAt": "2026-10-05T09:14:40.000Z" },
        { "id": "6701…03", "author": "AI", "body": "Export is limited to active subscriptions. Is the one you expect cancelled?", "createdAt": "2026-10-05T09:15:10.000Z" }
      ],
      "pageInfo": { "hasPreviousPage": false, "startCursor": "MjAyNi0xMC0w…" }
    }
  }
}`;

const CREATE = `mutation Open($input: CreateTicketInput!) {
  createTicket(input: $input) {
    ticket { id number status { name category } }
    message { id author body createdAt }
  }
}

{
  "input": {
    "typeId": "66f0…a2",
    "title": "Export button",
    "body": "The export button does nothing.",
    "clientMessageId": "1f0c2a3e-7d1b-4c6e-9a51-2b7c0e9d4f10"
  }
}`;

const SEND = `mutation Reply($input: SendMessageInput!) {
  sendMessage(input: $input) { id author body createdAt }
}

{
  "input": {
    "ticketId": "6700…ff",
    "body": "It happens on Chrome and Safari.",
    "clientMessageId": "8c2d5e0a-31f4-4b7a-a9c2-5d6e7f809a1b"
  }
}`;

const UPLOAD = `// 1. Upload each file: the raw bytes as the body, its type as Content-Type.
async function upload(file, ticketId) {
  const params = new URLSearchParams({ filename: file.name });
  if (ticketId) params.set('ticketId', ticketId); // omit when opening a new request
  const res = await fetch(\`https://api.revvly.ai/api/v1/support/chat/attachments?\${params}\`, {
    method: 'POST',
    headers: { Authorization: \`Bearer \${await token()}\`, 'Content-Type': file.type },
    body: file, // a File from <input type="file">
  });
  const json = await res.json();
  if (!res.ok) throw Object.assign(new Error(json.error.message), json.error);
  return json.attachment; // { id, filename, mimeType, size, url, previewUrl }
}

// 2. Send the ids with the message.
const files = await Promise.all([...input.files].map((f) => upload(f, openId)));
await chat(
  'mutation($input: SendMessageInput!) { sendMessage(input: $input) { id attachments { filename url } } }',
  { input: { ticketId: openId, body, clientMessageId: crypto.randomUUID(), attachmentIds: files.map((f) => f.id) } },
);`;

const MARK_READ = `mutation Seen($ticketId: ID!) {
  markRead(ticketId: $ticketId) { id unreadCount }
}`;

const NOTIFICATIONS = `mutation Prefs($input: EndUserNotificationPreferencesInput!) {
  updateMyNotificationPreferences(input: $input) { progressUpdates emailWhenOffline }
}`;

const CLOSE = `mutation Done($input: CloseTicketInput!) {
  closeTicket(input: $input) { id status { name category } closedAt }
}

{ "input": { "ticketId": "6700…ff", "reason": "SOLVED" } }`;

const REQUEST_HUMAN = `mutation Human($ticketId: ID!) {
  requestHuman(ticketId: $ticketId) { id status { name category } }
}`;

const RATE_ANSWER = `mutation Rate($messageId: ID!, $outcome: AnswerRating!) {
  rateAnswer(messageId: $messageId, outcome: $outcome) {
    id
    status { name category }
    closedAt
  }
}

{ "messageId": "6701…03", "outcome": "RESOLVED" }`;

const STREAM = `// Live updates in the browser. EventSource cannot send an Authorization
// header, so ask for a one-minute stream ticket and put it in the URL.
let lastEventId = null;

async function listen(onChange, onTyping) {
  const { createStreamTicket } = await chat('mutation { createStreamTicket { path } }');
  const resume = lastEventId ? \`&lastEventId=\${encodeURIComponent(lastEventId)}\` : '';
  const stream = new EventSource(\`https://api.revvly.ai\${createStreamTicket.path}\${resume}\`);

  stream.addEventListener('ready', (e) => {
    // resumed: false means updates may have been missed: re-read what is on screen.
    if (!JSON.parse(e.data).resumed) onChange(null);
  });
  stream.addEventListener('ticket_updated', (e) => {
    lastEventId = e.lastEventId;
    onChange(JSON.parse(e.data)); // { ticketId, change, messageId, by }: re-read that request
  });
  stream.addEventListener('typing', (e) => onTyping(JSON.parse(e.data)));
  // The token behind the stream is ending: reconnect with a fresh ticket.
  stream.addEventListener('reauthenticate', () => {
    stream.close();
    listen(onChange, onTyping);
  });
  stream.onerror = () => {
    stream.close(); // the ticket is spent; get a new one
    setTimeout(() => listen(onChange, onTyping), 5000);
  };
  return stream;
}`;

const STREAM_FRAMES = `event: ready
data: {"type":"ready","resumed":false}

id: 1791270098678-0
event: ticket_updated
data: {"type":"ticket_updated","streamId":"1791270098678-0","eventId":"6ac4…83",
       "ticketId":"6ac4…7c","change":"message","messageId":"6ac4…7d","by":"agent",
       "createdAt":"2026-10-06T07:01:38.486Z"}

event: typing
data: {"type":"typing","ticketId":"6ac4…7c","expiresInMs":6000}

event: reauthenticate
data: {"type":"reauthenticate"}`;

const WEBHOOK_BODY = `POST https://api.example.com/webhooks/revvly
Content-Type: application/json
Revvly-Event-Id: 6ac49cd2866904b09b303383
Revvly-Signature: t=1791270098,v1=5f2c…e1

{
  "id": "6ac49cd2866904b09b303383",
  "type": "ticket.message",
  "createdAt": "2026-10-06T07:01:38.486Z",
  "data": {
    "ticketId": "6ac49cd2866904b09b30337c",
    "ticketNumber": "AA-42",
    "messageId": "6ac49cd2866904b09b30337d",
    "by": "agent",
    "endUser": { "externalUserId": "user_8412" }
  }
}`;

const WEBHOOK_VERIFY = `import { createHmac, timingSafeEqual } from 'node:crypto';

// rawBody: the exact bytes received, before any JSON parsing.
function verify(rawBody, header, secret) {
  const parts = Object.fromEntries(String(header ?? '').split(',').map((p) => p.split('=')));
  const t = Number(parts.t);
  if (!Number.isFinite(t) || Math.abs(Date.now() / 1000 - t) > 300) return false; // older than 5 min
  const expected = createHmac('sha256', secret).update(\`\${parts.t}.\${rawBody}\`).digest('hex');
  const given = Buffer.from(parts.v1 ?? '');
  // timingSafeEqual throws on different lengths, so check that first.
  return given.length === expected.length && timingSafeEqual(Buffer.from(expected), given);
}`;

interface FieldDoc {
  name: string;
  badge?: 'required' | 'optional';
  text: ReactNode;
}

function Fields({ rows }: { rows: FieldDoc[] }) {
  return (
    <div className="dp-fields-table api-field-table-mb">
      {rows.map((row) => (
        <div className="dp-field-row" key={row.name}>
          <div className="dp-field-k">
            <span className="api-inline">{row.name}</span>
            {row.badge === 'required' && <span className="api-badge api-badge-req">required</span>}
            {row.badge === 'optional' && <span className="api-badge api-badge-opt">optional</span>}
          </div>
          <div className="dp-field-v">{row.text}</div>
        </div>
      ))}
    </div>
  );
}

function Op({ kind, name }: { kind: 'QUERY' | 'MUTATION'; name: string }) {
  return (
    <div className="api-endpoint-bar">
      <span className={`api-method api-method-${kind.toLowerCase()}`}>{kind}</span>
      <span className="api-url">{name}</span>
    </div>
  );
}

export function SupportChatApi() {
  useDocsPage('Support chat API — Revvly Docs');
  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/api">API</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Support chat</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">API Reference</span>
        <h1>
          Build your own chat widget on the <em>support chat API</em>.
        </h1>
        <p className="dp-oneliner">
          Everything a widget or in-app help panel needs to talk to your support team: list the
          signed-in user&rsquo;s requests, open a new one, reply, read the answers and close it.
          Called from the browser or your app with a short-lived token your backend mints.
        </p>
      </header>

      <OnThisPage endpoints={endpointsForPage('/docs/api/support-chat')} />

      <section className="dp-section">
        <h2>Before you start</h2>
        <p className="dp-section-sub">
          Your backend needs a support secret key and a route that mints a token for the user it has
          signed in. <Link to="/docs/guides/support-tokens">Sign users in to the support chat</Link>{' '}
          walks through both. Everything on this page is called with that token, not with a secret
          key.
        </p>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            This is a <strong>separate endpoint and schema</strong> from the{' '}
            <Link to="/docs/api/graphql-public">public GraphQL API</Link>. A token here can only
            reach its own user&rsquo;s requests; a secret key is not accepted here, and a token is
            not accepted there.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="support-chat">Endpoint &amp; authentication</AnchorHeading>
        <div className="api-endpoint-bar">
          <span className="api-method api-method-post">POST</span>
          <span className="api-url">https://api.revvly.ai/api/v1/support/graphql</span>
          <span className="api-returns">application/json</span>
        </div>
        <p className="dp-section-sub">
          Send a standard GraphQL request (a <span className="api-inline">query</span> and optional{' '}
          <span className="api-inline">variables</span>) as JSON.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Authorization</div>
            <div className="dp-field-v">
              <span className="api-inline">Bearer &lt;end-user token&gt;</span>, checked on every
              request: signature, expiry, and that the secret key that minted it is still live.
              Anything else ⇒ <span className="api-inline">UNAUTHENTICATED</span>; mint a fresh
              token and retry once.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Allowed origins</div>
            <div className="dp-field-v">
              A browser call must come from a site listed under Org Settings, Support, General,
              Widget allowed origins (https, origin only; https://*.example.com covers every subdomain but not the bare domain). Any other site ⇒{' '}
              <span className="api-inline">FORBIDDEN</span>, so a token copied off your site is
              useless on someone else&rsquo;s. Calls with no{' '}
              <span className="api-inline">Origin</span> header, from a native app or a server, are
              not checked. Changes take up to a minute to apply.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">CORS</div>
            <div className="dp-field-v">
              Preflights are answered for any origin. No cookies are used or allowed: send the token
              in the header, never with <span className="api-inline">credentials</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Rate limits</div>
            <div className="dp-field-v">
              Per user: 10 requests a second and 120 a minute, and for your whole workspace 200 a
              second and 6,000 a minute ⇒ <span className="api-inline">RATE_LIMITED</span> with a{' '}
              <span className="api-inline">Retry-After</span> header. At most 10 new requests per
              user an hour (a retried create does not count); over it,{' '}
              <span className="api-inline">createTicket</span> answers{' '}
              <span className="api-inline">RATE_LIMITED</span> with Retry-After set to when the
              oldest of those requests turns an hour old.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Query depth</div>
            <div className="dp-field-v">
              At most 8 levels; deeper queries ⇒{' '}
              <span className="api-inline">QUERY_DEPTH_EXCEEDED</span>. Introspection works with any
              valid token.
            </div>
          </div>
        </div>
        <span className="api-code-label">Request headers</span>
        <pre className="api-code">{`Authorization: Bearer <end-user token>
Content-Type:  application/json`}</pre>
        <span className="api-code-label">A small client with token refresh</span>
        <pre className="api-code">{CLIENT}</pre>
      </section>

      <section className="dp-section">
        <h2>Give this prompt to an integration agent</h2>
        <p className="dp-section-sub">
          The whole integration, backend route to widget, as one prompt. The step-by-step version is{' '}
          <Link to="/docs/guides/support-widget">Add a support chat to your app</Link>.
        </p>
        <pre className="api-code">{AGENT_PROMPT}</pre>
      </section>

      <section className="dp-section">
        <AnchorHeading id="privacy">What the user sees</AnchorHeading>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Only their own</div>
            <div className="dp-field-v">
              Every request comes back for the token&rsquo;s user only. Another user&rsquo;s request
              id answers <span className="api-inline">null</span> or{' '}
              <span className="api-inline">NOT_FOUND</span>, never{' '}
              <span className="api-inline">FORBIDDEN</span>, so nobody can probe which ids exist.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Never internal</div>
            <div className="dp-field-v">
              Internal notes, assignees, priorities, tags and agent names are never returned. A
              reply from your team has <span className="api-inline">author: AGENT</span>, with no
              name.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Your status names</div>
            <div className="dp-field-v">
              <span className="api-inline">status.name</span> is the name you gave the status in
              your workflow; branch on <span className="api-inline">status.category</span>, which is
              always one of four values.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="me">The user and the request types</AnchorHeading>
        <Op kind="QUERY" name="me · ticketTypes" />
        <p className="dp-section-sub">
          <span className="api-inline">me</span> echoes who the token is for, and{' '}
          <span className="api-inline">me.unreadCount</span> totals the replies the user has not
          read across all their requests: the number for the badge on your chat launcher, without
          listing every request. <span className="api-inline">ticketTypes</span> lists the kinds of
          request the user can open, in the order you set; show them as the first choice in your
          form.
        </p>
        <pre className="api-code">{ME}</pre>
        <span className="api-code-label">Response</span>
        <pre className="api-code">{ME_RESPONSE}</pre>
      </section>

      <section className="dp-section">
        <AnchorHeading id="my-tickets">List the user&rsquo;s requests</AnchorHeading>
        <Op kind="QUERY" name="myTickets" />
        <p className="dp-section-sub">
          Most recent conversation activity first. Use{' '}
          <span className="api-inline">unreadCount</span> for a badge.
        </p>
        <pre className="api-code">{MY_TICKETS}</pre>
        <h3 className="api-sub-heading">Arguments</h3>
        <Fields
          rows={[
            {
              name: 'statusCategory',
              badge: 'optional',
              text: (
                <>
                  Only requests in this category, e.g. <span className="api-inline">CLOSED</span>{' '}
                  for a history tab.
                </>
              ),
            },
            { name: 'first', badge: 'optional', text: 'Page size, 1 to 100. Default 20.' },
            {
              name: 'after',
              badge: 'optional',
              text: (
                <>
                  <span className="api-inline">pageInfo.endCursor</span> of the previous page.
                </>
              ),
            },
          ]}
        />
      </section>

      <section className="dp-section">
        <AnchorHeading id="ticket">Read one request</AnchorHeading>
        <Op kind="QUERY" name="ticket" />
        <pre className="api-code">{TICKET}</pre>
        <p className="dp-section-sub">
          Returns <span className="api-inline">null</span> for any id that is not the user&rsquo;s
          own.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="ticket-messages">Read the conversation</AnchorHeading>
        <Op kind="QUERY" name="ticketMessages" />
        <p className="dp-section-sub">
          The latest page of messages, oldest first within the page, ready to render top to bottom.
          For older messages pass <span className="api-inline">pageInfo.startCursor</span> as{' '}
          <span className="api-inline">before</span> and prepend the result.
        </p>
        <pre className="api-code">{MESSAGES}</pre>
        <span className="api-code-label">Response</span>
        <pre className="api-code">{MESSAGES_RESPONSE}</pre>
        <h3 className="api-sub-heading">Arguments</h3>
        <Fields
          rows={[
            { name: 'ticketId', badge: 'required', text: 'One of the user’s requests.' },
            { name: 'first', badge: 'optional', text: 'Page size, 1 to 100. Default 50.' },
            {
              name: 'before',
              badge: 'optional',
              text: (
                <>
                  <span className="api-inline">pageInfo.startCursor</span>, for the page before.
                </>
              ),
            },
          ]}
        />
        <p className="dp-section-sub">
          <span className="api-inline">author</span> is <span className="api-inline">YOU</span> (the
          user), <span className="api-inline">AGENT</span> (your team),{' '}
          <span className="api-inline">AI</span> (the AI assistant) or{' '}
          <span className="api-inline">SYSTEM</span>. Read{' '}
          <span className="api-inline">format</span> before rendering a body:{' '}
          <span className="api-inline">PLAIN</span> is text (show it as text, never as Markdown or
          HTML); <span className="api-inline">MARKDOWN</span> is a small subset, written by the AI
          and by your team, with numbered and bulleted lists, bold, italic, code and https links;
          replies from your team may also hold fenced code blocks and quotes (lines starting with{' '}
          <span className="api-inline">&gt;</span>). Nothing else appears, and a typed{' '}
          <span className="api-inline">&lt;</span> that could open an HTML tag arrives escaped with
          a backslash. Render it with a Markdown renderer that does not pass raw HTML, and open
          links in a new tab with{' '}
          <span className="api-inline">rel=&quot;noopener noreferrer&quot;</span>. In an AI reply a
          link only ever points at a help page or an address written in your own help content; in a
          reply from your team it is any https address the agent chose. Either way it is https only,
          so it is safe to make clickable. Replies stored before this existed stay{' '}
          <span className="api-inline">PLAIN</span>.
        </p>
        <p className="dp-section-sub">
          When the AI hands a request to your team, the request gets{' '}
          <span className="api-inline">handedOverAt</span> and a message arrives with{' '}
          <span className="api-inline">notice: AI_HANDOVER</span>. Show that message as a notice
          (&quot;A teammate will reply&quot;) rather than as a chat bubble, and keep showing it
          until an <span className="api-inline">AGENT</span> message comes.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="create-ticket">Open a request</AnchorHeading>
        <Op kind="MUTATION" name="createTicket" />
        <p className="dp-section-sub">
          Opens a request with its first message. It lands in your Support inbox, unassigned, and
          notifies your agents.
        </p>
        <pre className="api-code">{CREATE}</pre>
        <h3 className="api-sub-heading">Input</h3>
        <Fields
          rows={[
            {
              name: 'typeId',
              badge: 'required',
              text: (
                <>
                  One of <span className="api-inline">ticketTypes</span>.
                </>
              ),
            },
            {
              name: 'body',
              badge: 'required',
              text: 'The first message, plain text, up to 20,000 characters.',
            },
            { name: 'title', badge: 'optional', text: 'A subject, up to 200 characters.' },
            {
              name: 'clientMessageId',
              badge: 'required',
              text: (
                <>
                  A unique id you generate per send, such as{' '}
                  <span className="api-inline">crypto.randomUUID()</span> (max 100 characters).
                  Retrying with the same id returns the request the first attempt opened instead of
                  opening a second one, so retry freely after a timeout.
                </>
              ),
            },
            {
              name: 'attachmentIds',
              badge: 'optional',
              text: 'Up to 10 files uploaded without a ticketId (see Attach files).',
            },
            {
              name: 'source',
              badge: 'optional',
              text: 'Where in your product the request was opened, such as web, ios or billing-page: a letter or digit, then up to 63 letters, digits, _ - . / or :. Agents see it on the request and can filter the inbox by it.',
            },
          ]}
        />
      </section>

      <section className="dp-section">
        <AnchorHeading id="send-message">Reply</AnchorHeading>
        <Op kind="MUTATION" name="sendMessage" />
        <p className="dp-section-sub">
          Adds a message to one of the user&rsquo;s requests. A reply to a closed request reopens
          it. <span className="api-inline">clientMessageId</span> works as for{' '}
          <span className="api-inline">createTicket</span>: the same id returns the original
          message.
        </p>
        <pre className="api-code">{SEND}</pre>
      </section>

      <section className="dp-section">
        <AnchorHeading id="attachments">Attach files</AnchorHeading>
        <div className="api-endpoint-bar">
          <span className="api-method api-method-post">POST</span>
          <span className="api-url">https://api.revvly.ai/api/v1/support/chat/attachments</span>
          <span className="api-returns">application/json</span>
        </div>
        <p className="dp-section-sub">
          Upload each file first, then pass the returned ids as{' '}
          <span className="api-inline">attachmentIds</span> on{' '}
          <span className="api-inline">sendMessage</span>, or on{' '}
          <span className="api-inline">createTicket</span> for files uploaded without a{' '}
          <span className="api-inline">ticketId</span>. Up to 10 files a message.
        </p>
        <pre className="api-code">{UPLOAD}</pre>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Files</div>
            <div className="dp-field-v">
              PNG, JPEG, GIF, WebP, PDF or plain text, up to 20 MB. The type is checked against the
              file&rsquo;s bytes, not just its name, and files are scanned for malware wherever a
              virus scanner is configured. Anything else ⇒{' '}
              <span className="api-inline">BAD_REQUEST</span>; over 20 MB ⇒ 413.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Who can use a file</div>
            <div className="dp-field-v">
              Only the user who uploaded it, and only on the request it was uploaded for. A file is
              kept a day if no message uses it.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Showing files</div>
            <div className="dp-field-v">
              Each message lists its <span className="api-inline">attachments</span>. Use{' '}
              <span className="api-inline">previewUrl</span> for an inline image (a re-encoded copy)
              and <span className="api-inline">url</span> for a download. Both last 30 minutes: read
              the message again for fresh ones.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Limits and errors</div>
            <div className="dp-field-v">
              The chat API&rsquo;s auth, origin check and per-user limit, plus 2 uploads a second
              and 20 a minute. Errors come back as{' '}
              <span className="api-inline">{'{ "error": { "code", "message" } }'}</span> with the
              same codes.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="mark-read">Mark as read</AnchorHeading>
        <Op kind="MUTATION" name="markRead" />
        <p className="dp-section-sub">
          Call it when the user actually sees the conversation. It sets{' '}
          <span className="api-inline">unreadCount</span> back to 0 and cancels the email Revvly
          would otherwise send about the unread reply (sent only to users whose token carries an
          email, after the delay set in Support settings).
        </p>
        <pre className="api-code">{MARK_READ}</pre>
      </section>

      <section className="dp-section">
        <AnchorHeading id="notifications">Notification preferences</AnchorHeading>
        <Op kind="MUTATION" name="updateMyNotificationPreferences" />
        <p className="dp-section-sub">
          The email about an unread reply is on by default. The user can turn it off from your chat
          settings with <span className="api-inline">emailWhenOffline: false</span>, or with the
          &ldquo;Turn off these emails&rdquo; link at the foot of every such email; read the current
          choice with <span className="api-inline">myNotificationPreferences</span>. The choice
          belongs to the user: minting a token or syncing profiles never changes it, so your backend
          cannot turn the emails back on for them. Fields you leave out stay as they are.
        </p>
        <p className="dp-section-sub">
          <span className="api-inline">progressUpdates</span> (
          <span className="api-inline">ALL</span>, <span className="api-inline">FINAL</span> or{' '}
          <span className="api-inline">NONE</span>) is the user&rsquo;s choice about progress
          updates on the work behind a request. It controls the stage notices a client receives as
          the work moves forward, so offer it in your settings screen.
        </p>
        <pre className="api-code">{NOTIFICATIONS}</pre>
      </section>

      <section className="dp-section">
        <AnchorHeading id="close-ticket">Close a request</AnchorHeading>
        <Op kind="MUTATION" name="closeTicket" />
        <p className="dp-section-sub">
          Lets the user close their own request, with an optional{' '}
          <span className="api-inline">reason</span> (<span className="api-inline">SOLVED</span>,{' '}
          <span className="api-inline">NO_LONGER_NEEDED</span>,{' '}
          <span className="api-inline">OTHER</span>) and <span className="api-inline">comment</span>{' '}
          (up to 2,000 characters). Closing a closed request returns it unchanged; a new message
          reopens it.
        </p>
        <pre className="api-code">{CLOSE}</pre>
      </section>

      <section className="dp-section">
        <AnchorHeading id="request-human">Ask for a person</AnchorHeading>
        <Op kind="MUTATION" name="requestHuman" />
        <p className="dp-section-sub">
          Lets the user leave the AI assistant and hand the request to your support team. It is
          always allowed, whatever the assistant has said. The assistant stops answering that
          request, the user sees a short hand-over message, and your team takes it. Calling it again
          changes nothing and sends no second message. Use it behind a &ldquo;Talk to a
          person&rdquo; button.
        </p>
        <pre className="api-code">{REQUEST_HUMAN}</pre>
        <p className="dp-section-sub">
          Returns the updated <span className="api-inline">Ticket</span>.{' '}
          <span className="api-inline">NOT_FOUND</span> if the request is not the user&rsquo;s own.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="rate-answer">Rate an AI reply</AnchorHeading>
        <Op kind="MUTATION" name="rateAnswer" />
        <p className="dp-section-sub">
          Lets the user judge one reply written by the AI assistant (a message whose{' '}
          <span className="api-inline">author</span> is <span className="api-inline">AI</span>).
          Pass its id as <span className="api-inline">messageId</span> and one of two outcomes.
        </p>
        <Fields
          rows={[
            {
              name: 'RESOLVED',
              text: 'This solved it. The request closes as solved by the assistant. It is refused once a person has the request: it was closed, handed over, assigned, or an agent has replied in public.',
            },
            {
              name: 'NOT_HELPFUL',
              text: 'It did not help. The assistant stops and a person takes over, as with requestHuman.',
            },
          ]}
        />
        <pre className="api-code">{RATE_ANSWER}</pre>
        <p className="dp-section-sub">
          A reply is rated once. Sending the same outcome again is accepted and changes nothing;
          sending a different one is refused with <span className="api-inline">BAD_REQUEST</span>,
          as is a message id that is unknown, is not an AI reply, or is not on one of the
          user&rsquo;s own requests. Returns the updated <span className="api-inline">Ticket</span>.
          Offer the two buttons under an AI reply.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="types">Types</AnchorHeading>
        <h3 className="api-sub-heading">Ticket</h3>
        <Fields
          rows={[
            { name: 'id', text: 'Stable id.' },
            {
              name: 'number',
              text: 'The reference the user and your agents both see, e.g. AA-42.',
            },
            { name: 'title', text: 'Null until the user or an agent gives it one.' },
            { name: 'type', text: 'TicketType: id, key, name.' },
            { name: 'status', text: 'TicketStatus: key, name (yours), category.' },
            { name: 'createdAt · lastMessageAt · closedAt', text: 'ISO 8601 timestamps.' },
            { name: 'unreadCount', text: 'Replies the user has not read yet.' },
            {
              name: 'progress',
              text: 'How far the work behind the request has got, in steps you name in Settings (key, label, reachedAt). Null until work starts. Never a tracker status.',
            },
            {
              name: 'handedOverAt',
              text: 'ISO 8601, set once the AI has handed the request to your team; null before. Show "A teammate will reply" while it is set and no AGENT message has come.',
            },
          ]}
        />
        <h3 className="api-sub-heading">StatusCategory</h3>
        <Fields
          rows={[
            { name: 'OPEN', text: 'New, waiting for your team.' },
            { name: 'IN_PROGRESS', text: 'Your team is working on it.' },
            {
              name: 'PENDING',
              text: 'Waiting on the user. Closes by itself after the quiet period you set, unless the user replies.',
            },
            { name: 'CLOSED', text: 'Closed. A new message reopens it.' },
          ]}
        />
        <h3 className="api-sub-heading">Message</h3>
        <Fields
          rows={[
            { name: 'id · ticketId', text: 'Ids.' },
            { name: 'author', text: 'YOU, AGENT, AI or SYSTEM.' },
            { name: 'body', text: 'The text. Read format to know how.' },
            {
              name: 'format',
              text: "PLAIN for the user's own messages and system notices; MARKDOWN for AI and team replies (the subset above).",
            },
            {
              name: 'notice',
              text: 'AI_HANDOVER on the system message that says the AI handed over; null on every other message.',
            },
            {
              name: 'attachments',
              text: 'id, filename, mimeType, size, url (download) and previewUrl (images only). Links last 30 minutes.',
            },
            {
              name: 'citations',
              text: 'AI replies only: the help pages the answer is taken from (title, url, anchor). Empty for every other author. Show them as sources under the message.',
            },
            { name: 'createdAt', text: 'ISO 8601.' },
          ]}
        />
      </section>

      <section className="dp-section">
        <AnchorHeading id="updates">Live updates</AnchorHeading>
        <p className="dp-section-sub">
          Open one stream per signed-in user while your chat is open, and re-read what it says
          changed. Frames say which request changed and how, never the content: you fetch that with
          the queries above, so what a user sees always goes through the same rules.
        </p>
        <div className="api-endpoint-bar">
          <span className="api-method api-method-get">GET</span>
          <span className="api-url">https://api.revvly.ai/api/v1/support/chat/stream</span>
          <span className="api-returns">text/event-stream</span>
        </div>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">From a browser</div>
            <div className="dp-field-v">
              Call <span className="api-inline">createStreamTicket</span> (below), then open{' '}
              <span className="api-inline">EventSource(origin + path)</span>. The ticket lasts a
              minute and works only on this endpoint.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">From an app or server</div>
            <div className="dp-field-v">
              Send <span className="api-inline">Authorization: Bearer &lt;end-user token&gt;</span>{' '}
              with any client that reads server-sent events.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Resume</div>
            <div className="dp-field-v">
              Send the last <span className="api-inline">id</span> you received as{' '}
              <span className="api-inline">Last-Event-ID</span> or{' '}
              <span className="api-inline">?lastEventId=</span>. Missed frames arrive before{' '}
              <span className="api-inline">ready</span>, which then says{' '}
              <span className="api-inline">resumed: true</span>. With{' '}
              <span className="api-inline">resumed: false</span>, re-read what is on screen.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Lifetime</div>
            <div className="dp-field-v">
              The stream ends with <span className="api-inline">reauthenticate</span> when the token
              expires, and after 15 minutes at most, so a revoked key or a changed role reaches open
              connections. Reconnect with a fresh ticket or token.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Limits</div>
            <div className="dp-field-v">
              The same origin check and per-user rate limit as GraphQL; opening a stream counts as
              one request. 503 means live updates are unavailable: fall back to reading every few
              seconds.
            </div>
          </div>
        </div>
        <span className="api-code-label">Frames</span>
        <pre className="api-code">{STREAM_FRAMES}</pre>
        <Fields
          rows={[
            {
              name: 'ticket_updated.change',
              text: 'created, message, status, type, title, closed, reopened or progress. Delivery is at least once: dedupe on eventId.',
            },
            {
              name: 'ticket_updated.by',
              text: 'you, agent, ai or system. Agents are never named.',
            },
            {
              name: 'typing',
              text: 'Your support team is writing a reply on that request. Show it for expiresInMs unless another frame arrives. Internal notes are never announced.',
            },
          ]}
        />
        <Op kind="MUTATION" name="createStreamTicket" />
        <span className="api-code-label">A browser listener</span>
        <pre className="api-code">{STREAM}</pre>
      </section>

      <section className="dp-section">
        <AnchorHeading id="webhook">Live updates through your backend</AnchorHeading>
        <p className="dp-section-sub">
          If your users&rsquo; browsers talk to your backend and your backend talks to Revvly, let
          Revvly post each change to your backend instead and pass it on to the right user. Add an
          endpoint under Org Settings, Support, Webhooks and choose its events. Each endpoint has
          its own signing secret, shown once, and you can add up to five.
        </p>
        <pre className="api-code">{WEBHOOK_BODY}</pre>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Events</div>
            <div className="dp-field-v">
              <span className="api-inline">type</span> is one of{' '}
              <span className="api-inline">ticket.created</span>,{' '}
              <span className="api-inline">ticket.message</span>,{' '}
              <span className="api-inline">ticket.status_changed</span>,{' '}
              <span className="api-inline">ticket.updated</span> (type or title),{' '}
              <span className="api-inline">ticket.closed</span>,{' '}
              <span className="api-inline">ticket.reopened</span> and{' '}
              <span className="api-inline">ticket.progressed</span> (the client stage moved). Each
              endpoint gets only the ones you chose.{' '}
              <span className="api-inline">webhook.test</span> is sent by the Test button.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">What is sent</div>
            <div className="dp-field-v">
              Every change a user can see on a request they opened, with{' '}
              <span className="api-inline">endUser.externalUserId</span> so you know whose it is.
              Ids only: read the content from this API as that user. The one exception is{' '}
              <span className="api-inline">ticket.progressed</span>, whose{' '}
              <span className="api-inline">data.stage</span> is{' '}
              <span className="api-inline">{'{ key, label }'}</span>, the step you named in
              Settings.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Signature</div>
            <div className="dp-field-v">
              <span className="api-inline">
                Revvly-Signature: t=&lt;unix seconds&gt;,v1=&lt;hex&gt;
              </span>
              , where v1 is HMAC-SHA256 of <span className="api-inline">t.body</span> with your
              secret. Reject anything that does not match or is more than 5 minutes old.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Delivery</div>
            <div className="dp-field-v">
              Answer 2xx within 5 seconds. Anything else is retried after 10 s, 1 min, 5 min, 30
              min, 2 h and 6 h, then dropped. At least once and possibly out of order: dedupe on{' '}
              <span className="api-inline">Revvly-Event-Id</span>. https only; redirects are not
              followed.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Failing endpoints</div>
            <div className="dp-field-v">
              An endpoint whose deliveries give up ten times in a row is switched off, and nothing
              more is queued for it until an admin turns it on. One success resets the count.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Replay</div>
            <div className="dp-field-v">
              Every delivery is kept for 30 days under Support, Webhooks, Deliveries. Replay sends a
              finished one again with the same body and event id, a fresh signature, and a new run
              of retries.
            </div>
          </div>
        </div>
        <span className="api-code-label">Check the signature (Node)</span>
        <pre className="api-code">{WEBHOOK_VERIFY}</pre>
      </section>

      <section className="dp-section">
        <AnchorHeading id="errors">Errors</AnchorHeading>
        <p className="dp-section-sub">
          Errors come back in the standard GraphQL <span className="api-inline">errors</span> array,
          each with a stable <span className="api-inline">extensions.code</span>.
        </p>
        <Fields
          rows={[
            {
              name: 'UNAUTHENTICATED',
              text: 'No token, or it is invalid, expired or its secret key was revoked. Mint a new token and retry once.',
            },
            {
              name: 'FORBIDDEN',
              text: 'The page’s origin is not in your widget allowed origins.',
            },
            {
              name: 'NOT_FOUND',
              text: 'The request does not exist or is not the user’s own.',
            },
            {
              name: 'BAD_REQUEST',
              text: 'Invalid input: an empty or too long body or title, an unknown or retired type, a bad cursor or page size, a missing clientMessageId.',
            },
            {
              name: 'RATE_LIMITED',
              text: 'Over the per-user rate limit or the new-request limit. Wait for the Retry-After seconds.',
            },
            {
              name: 'INTERNAL_ERROR',
              text: 'Something failed on our side. Safe to retry with the same clientMessageId.',
            },
          ]}
        />
      </section>

      <section className="dp-section">
        <AnchorHeading id="try-it">Try it from the dashboard</AnchorHeading>
        <p className="dp-section-sub">
          The Sandbox link at the foot of the Support inbox opens a page that chats through this
          exact API with a token for a practice customer you choose, so you can see what a user will
          see before you write the widget. The sandbox is a hidden copy of your Support setup:
          nothing in it reaches your real inbox, metrics or email.
        </p>
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/guides/support-setup">Set up Support for your team ›</Link>
          </li>
          <li>
            <Link to="/docs/guides/support-widget">Add a support chat to your app ›</Link>
          </li>
          <li>
            <Link to="/docs/guides/support-tokens">Sign users in to the support chat ›</Link>
          </li>
          <li>
            <Link to="/docs/api/graphql-public">Public GraphQL API ›</Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
